import { z } from 'zod';
import { STATUS_TYPES, type StatusType } from './constants';
import type { FilterGroup } from './tasks';

// ---------- Triggers ----------

export const TRIGGER_TYPES = [
  'task_created',
  'status_changed',
  'assignee_changed',
  'priority_changed',
  'due_date_arrives',
  'field_changed',
  'comment_added',
  'form_submitted',
  'moved_to_list',
] as const;
export type TriggerType = (typeof TRIGGER_TYPES)[number];

export type AutomationTrigger =
  | { type: 'task_created' }
  /** Statuses are matched by name or type, since every list may have its own status set. */
  | { type: 'status_changed'; toStatusName?: string; toStatusType?: StatusType; fromStatusName?: string }
  | { type: 'assignee_changed'; userId?: string }
  | { type: 'priority_changed'; priority?: number }
  /** Fires when the due date is reached (offset in minutes: negative means before). */
  | { type: 'due_date_arrives'; offsetMinutes?: number }
  | { type: 'field_changed'; fieldId?: string }
  | { type: 'comment_added' }
  | { type: 'form_submitted'; formId?: string }
  | { type: 'moved_to_list'; listId?: string };

// ---------- Actions ----------

export const ACTION_TYPES = [
  'change_status',
  'assign',
  'unassign',
  'set_priority',
  'set_field',
  'add_tag',
  'move_to_list',
  'add_to_list',
  'create_subtask',
  'add_comment',
  'notify',
  'send_email',
  'create_from_template',
  'webhook',
] as const;
export type ActionType = (typeof ACTION_TYPES)[number];

/** Who an action targets: specific people, the task's assignees, its creator, or the person who triggered it. */
export type ActionAudience = { userIds?: string[]; assignees?: boolean; creator?: boolean; actor?: boolean };

export type AutomationAction =
  | { type: 'change_status'; statusName?: string; statusType?: StatusType }
  | { type: 'assign'; userIds: string[] }
  | { type: 'unassign'; userIds?: string[]; all?: boolean }
  | { type: 'set_priority'; priority: number }
  | { type: 'set_field'; fieldId: string; value: unknown }
  | { type: 'add_tag'; tagId: string }
  | { type: 'move_to_list'; listId: string }
  | { type: 'add_to_list'; listId: string }
  | { type: 'create_subtask'; name: string; assigneeIds?: string[] }
  | { type: 'add_comment'; text: string }
  | { type: 'notify'; to: ActionAudience; text: string }
  | { type: 'send_email'; to: ActionAudience; subject: string; text: string }
  | { type: 'create_from_template'; templateId: string; listId?: string }
  | { type: 'webhook'; url: string; secret?: string };

export interface AutomationDTO {
  id: string;
  scopeType: 'workspace' | 'space' | 'folder' | 'list';
  scopeId: string;
  name: string;
  enabled: boolean;
  trigger: AutomationTrigger;
  conditions: FilterGroup | null;
  actions: AutomationAction[];
  runCount: number;
  lastRunAt: number | null;
  createdBy: string;
  createdAt: number;
  updatedAt: number;
}

export interface AutomationRunDTO {
  id: string;
  automationId: string;
  taskId: string | null;
  trigger: string;
  status: 'success' | 'failed' | 'skipped';
  error: string | null;
  log: string[];
  createdAt: number;
}

export interface AutomationChangedEventData {
  automationId: string;
  op: 'created' | 'updated' | 'deleted' | 'run';
}

/** Automations nest at most this deep (an automation's action firing another automation). */
export const MAX_AUTOMATION_DEPTH = 3;

const id = z.string().min(1).max(64);
const ids = z.array(id).max(50);
const text = z.string().max(2000);

export const triggerSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('task_created') }),
  z.object({ type: z.literal('status_changed'), toStatusName: z.string().max(80).optional(), toStatusType: z.enum(STATUS_TYPES).optional(), fromStatusName: z.string().max(80).optional() }),
  z.object({ type: z.literal('assignee_changed'), userId: id.optional() }),
  z.object({ type: z.literal('priority_changed'), priority: z.number().int().min(0).max(4).optional() }),
  z.object({ type: z.literal('due_date_arrives'), offsetMinutes: z.number().int().min(-10_080).max(10_080).optional() }),
  z.object({ type: z.literal('field_changed'), fieldId: id.optional() }),
  z.object({ type: z.literal('comment_added') }),
  z.object({ type: z.literal('form_submitted'), formId: id.optional() }),
  z.object({ type: z.literal('moved_to_list'), listId: id.optional() }),
]);

const audience = z.object({ userIds: ids.optional(), assignees: z.boolean().optional(), creator: z.boolean().optional(), actor: z.boolean().optional() });

export const actionSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('change_status'), statusName: z.string().max(80).optional(), statusType: z.enum(STATUS_TYPES).optional() }),
  z.object({ type: z.literal('assign'), userIds: ids.min(1) }),
  z.object({ type: z.literal('unassign'), userIds: ids.optional(), all: z.boolean().optional() }),
  z.object({ type: z.literal('set_priority'), priority: z.number().int().min(0).max(4) }),
  z.object({ type: z.literal('set_field'), fieldId: id, value: z.unknown() }),
  z.object({ type: z.literal('add_tag'), tagId: id }),
  z.object({ type: z.literal('move_to_list'), listId: id }),
  z.object({ type: z.literal('add_to_list'), listId: id }),
  z.object({ type: z.literal('create_subtask'), name: z.string().trim().min(1).max(500), assigneeIds: ids.optional() }),
  z.object({ type: z.literal('add_comment'), text: text.min(1) }),
  z.object({ type: z.literal('notify'), to: audience, text: text.min(1) }),
  z.object({ type: z.literal('send_email'), to: audience, subject: z.string().trim().min(1).max(200), text: text.min(1) }),
  z.object({ type: z.literal('create_from_template'), templateId: id, listId: id.optional() }),
  z.object({ type: z.literal('webhook'), url: z.string().url().max(500), secret: z.string().max(200).optional() }),
]);

export const createAutomationSchema = z.object({
  scopeType: z.enum(['workspace', 'space', 'folder', 'list']),
  scopeId: id.optional(),
  name: z.string().trim().min(1).max(120),
  enabled: z.boolean().optional(),
  trigger: triggerSchema,
  conditions: z.record(z.string(), z.unknown()).nullable().optional(),
  actions: z.array(actionSchema).min(1).max(20),
});
export type CreateAutomationInput = z.infer<typeof createAutomationSchema>;

export const updateAutomationSchema = createAutomationSchema.partial();
export type UpdateAutomationInput = z.infer<typeof updateAutomationSchema>;

/** Placeholders in comment, notification and email text. */
export function fillTemplate(text: string, vars: Record<string, string | number | null | undefined>): string {
  return text.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key: string) => {
    const v = vars[key];
    return v === null || v === undefined ? '' : String(v);
  });
}

// ---------- Templates ----------

export interface TaskTemplateData {
  name: string;
  description?: string;
  priority?: number;
  assigneeIds?: string[];
  tagIds?: string[];
  fields?: Record<string, unknown>;
  timeEstimateMs?: number | null;
  /** Due date relative to when the template is applied, in days. */
  dueInDays?: number | null;
  checklists?: { name: string; items: string[] }[];
  subtasks?: { name: string; assigneeIds?: string[] }[];
}

export interface ListTemplateData {
  statuses?: { name: string; color: string; type: StatusType }[];
  fields?: { name: string; type: string; config?: Record<string, unknown> }[];
  tasks?: TaskTemplateData[];
}

export interface TemplateDTO {
  id: string;
  type: 'task' | 'list';
  name: string;
  description: string | null;
  data: TaskTemplateData | ListTemplateData;
  createdBy: string;
  createdAt: number;
  updatedAt: number;
}

export const createTemplateSchema = z.object({
  type: z.enum(['task', 'list']),
  name: z.string().trim().min(1).max(120),
  description: z.string().max(500).nullable().optional(),
  data: z.record(z.string(), z.unknown()),
});
export type CreateTemplateInput = z.infer<typeof createTemplateSchema>;
export const updateTemplateSchema = createTemplateSchema.omit({ type: true }).partial();
export type UpdateTemplateInput = z.infer<typeof updateTemplateSchema>;

export const applyTemplateSchema = z.object({
  /** Task templates: the list (and optional parent task) to create in. */
  listId: id.optional(),
  parentId: id.optional(),
  /** List templates: where to create the list and what to call it. */
  spaceId: id.optional(),
  folderId: id.nullable().optional(),
  name: z.string().trim().min(1).max(120).optional(),
});
export type ApplyTemplateInput = z.infer<typeof applyTemplateSchema>;

// ---------- Webhooks and tokens ----------

export interface WebhookDTO {
  id: string;
  url: string;
  events: string[];
  enabled: boolean;
  lastStatus: number | null;
  lastDeliveredAt: number | null;
  failureCount: number;
  createdBy: string;
  createdAt: number;
}

export const createWebhookSchema = z.object({
  url: z.string().url().max(500),
  events: z.array(z.string().max(60)).min(1).max(40),
  enabled: z.boolean().optional(),
});
export type CreateWebhookInput = z.infer<typeof createWebhookSchema>;
export const updateWebhookSchema = createWebhookSchema.partial();
export type UpdateWebhookInput = z.infer<typeof updateWebhookSchema>;

export interface ApiTokenDTO {
  id: string;
  name: string;
  prefix: string;
  lastUsedAt: number | null;
  expiresAt: number | null;
  /** Set when an admin made the token for the member: it works only in that workspace. */
  workspaceId: string | null;
  /** Who made it, when that was not the token's own user. */
  createdBy: string | null;
  createdAt: number;
}

export const createApiTokenSchema = z.object({
  name: z.string().trim().min(1).max(80),
  /** Days until it expires; omit for no expiry. */
  expiresInDays: z.number().int().min(1).max(3650).optional(),
});

export const pushSubscribeSchema = z.object({
  endpoint: z.string().url().max(1000),
  keys: z.object({ p256dh: z.string().max(200), auth: z.string().max(100) }),
});

// ---------- Audit ----------

export interface AuditEntryDTO {
  id: string;
  resourceType: string;
  resourceId: string;
  actorId: string | null;
  action: string;
  data: Record<string, unknown> | null;
  createdAt: number;
}

export const auditQuerySchema = z.object({
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(200).optional(),
  actorId: z.string().max(64).optional(),
  resourceType: z.string().max(32).optional(),
  q: z.string().max(100).optional(),
});
